import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';

import useSharedStyles from '../theme/pbdqShared.styles';
import { pbdqFont } from '../theme/pbdqTokens';
import { sdsColors } from '../theme/solutionsDesignSystem';
import pbdqRoutes from '../pbdqRoutes';
import { PBDQ_SOLUTIONS, PBDQ_SOLUTIONS_CONFIG } from '../constants/pbdqSolutions.constants';
import DqLogo from '../../../assets/images/PoweredByDQ/HomeHeroDqLogo.png';

const useNavDropdownStyles = makeStyles(() => ({
    dropdownWrap: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        height: '100%',
        '&:hover $dropdownMenu': {
            opacity: 1,
            visibility: 'visible',
            transform: 'translateY(0)',
            pointerEvents: 'auto',
        },
    },
    solutionsNavLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '26px 0 24px',
        borderBottom: '2px solid transparent',
        whiteSpace: 'nowrap',
        fontSize: 15,
        fontWeight: 500,
        color: '#1B2430',
        fontFamily: pbdqFont,
        cursor: 'pointer',
        transition: 'color 120ms',
        '&:hover': {
            color: sdsColors.accent,
        },
    },
    chevronIcon: {
        width: 14,
        height: 14,
        transition: 'transform 200ms ease',
        '$dropdownWrap:hover &': {
            transform: 'rotate(180deg)',
        },
    },
    dropdownMenu: {
        position: 'absolute',
        top: 'calc(100% - 14px)',
        left: 0,
        transform: 'translateY(4px)',
        width: 'max-content',
        background: '#FFFFFF',
        borderRadius: 8,
        boxShadow: '0 8px 24px -4px rgba(15, 28, 39, 0.1), 0 1px 3px rgba(15, 28, 39, 0.05)',
        border: '1px solid #EAECEF',
        padding: '4px 0',
        overflow: 'hidden',
        opacity: 0,
        visibility: 'hidden',
        pointerEvents: 'none',
        transition: 'opacity 140ms ease, transform 140ms ease, visibility 140ms',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        // Invisible bridge to prevent hover loss between link and menu
        '&::before': {
            content: '""',
            position: 'absolute',
            top: -14,
            left: 0,
            right: 0,
            height: 16,
        },
    },
    dropdownItem: {
        display: 'flex !important',
        alignItems: 'center',
        gap: 9,
        padding: '10px 18px !important',
        margin: 0,
        borderRadius: 0,
        width: '100%',
        textDecoration: 'none !important',
        color: '#2C3540 !important',
        fontFamily: pbdqFont,
        fontSize: '13px !important',
        fontWeight: '500 !important',
        lineHeight: '1.2 !important',
        transition: 'background 100ms ease, color 100ms ease',
        cursor: 'pointer',
        borderBottom: 'none !important',
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
        '&:hover': {
            background: '#F5F7FA',
            color: `${sdsColors.accent} !important`,
        },
    },
    dropdownDot: {
        width: 6,
        height: 6,
        borderRadius: '50%',
        flexShrink: 0,
        opacity: 0.9,
    },
    dropdownItemTitle: {
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.2,
        color: 'inherit',
        whiteSpace: 'nowrap',
    },
}));

// The source highlights the current nav destination (color: var(--color-accent),
// aria-current="page"). "Solutions" stays highlighted across every solution
// sub-page (COS Assessments, School COS Readiness, LearnCreator, Workforce AI
// Readiness, Program Validation, DQEveryChild) — verified directly against the
// source mockup across all 13 pages, not a guess.
const SOLUTIONS_GROUP = [
    pbdqRoutes.solutions,
    pbdqRoutes.cosAssessments,
    pbdqRoutes.schoolCosReadiness,
    pbdqRoutes.learnCreator,
    pbdqRoutes.workforceAiReadiness,
    pbdqRoutes.programValidation,
    pbdqRoutes.dqEveryChild,
];

const ChevronDownIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m6 9 6 6 6-6" />
    </svg>
);

const ArrowRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14" />
        <path d="m12 5 7 7-7 7" />
    </svg>
);

const PbdqNav = () => {
    const classes = useSharedStyles();
    const dropdownClasses = useNavDropdownStyles();
    const { pathname, hash } = useLocation();

    useEffect(() => {
        if (!hash) {
            window.scrollTo(0, 0);
        }
    }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    const isActive = (paths) => paths.includes(pathname);
    const activeStyle = { color: sdsColors.accent, fontWeight: 600 };

    const solutionsActive = isActive(SOLUTIONS_GROUP);
    const capabilitiesActive = isActive([pbdqRoutes.capabilities]);
    const impactActive = isActive([pbdqRoutes.impact]);
    const partnersActive = isActive([pbdqRoutes.pricing]);
    const getStartedActive = isActive([pbdqRoutes.getStarted]);

    return (
        <header className={classes.navHeader}>
            <nav className={classes.nav} aria-label="Primary">
                <span className={classes.navBrand}>
                    <Link to={pbdqRoutes.home} className={classes.navLogoLink}>
                        <span className={classes.navLogoText}>
                            Powered
                            <br />
                            by
                        </span>
                        <img src={DqLogo} alt="DQ" className={classes.navLogoImg} />
                    </Link>
                    <span className={classes.navSlogan}>
                        The engine behind
                        <br />
                        AI-digital literacy building
                    </span>
                </span>
                <div className={classes.navLinks}>
                    <div className={dropdownClasses.dropdownWrap}>
                        <Link
                            to={pbdqRoutes.solutions}
                            className={dropdownClasses.solutionsNavLink}
                            style={solutionsActive ? activeStyle : undefined}
                            aria-current={solutionsActive ? 'page' : undefined}
                        >
                            <span>Solutions</span>
                            <ChevronDownIcon className={dropdownClasses.chevronIcon} />
                        </Link>
                        <div className={dropdownClasses.dropdownMenu} role="menu" aria-label="Solutions menu">
                            {PBDQ_SOLUTIONS.map((s, idx) => {
                                const cfg = PBDQ_SOLUTIONS_CONFIG[idx % PBDQ_SOLUTIONS_CONFIG.length];
                                return (
                                    <Link
                                        key={s.index}
                                        to={s.to}
                                        role="menuitem"
                                        className={dropdownClasses.dropdownItem}
                                    >
                                        <span
                                            className={dropdownClasses.dropdownDot}
                                            style={{ background: cfg.bar }}
                                        />
                                        <span className={dropdownClasses.dropdownItemTitle}>
                                            {s.shortTitle || s.title}
                                        </span>
                                    </Link>
                                );
                            })}
                            <Link
                                to={pbdqRoutes.dqEveryChild}
                                role="menuitem"
                                className={dropdownClasses.dropdownItem}
                            >
                                <span
                                    className={dropdownClasses.dropdownDot}
                                    style={{ background: '#052F44' }}
                                />
                                <span className={dropdownClasses.dropdownItemTitle}>
                                    #DQEveryChild
                                </span>
                            </Link>
                        </div>
                    </div>
                    <Link
                        to={pbdqRoutes.capabilities}
                        style={capabilitiesActive ? activeStyle : undefined}
                        aria-current={capabilitiesActive ? 'page' : undefined}
                    >
                        Capabilities
                    </Link>
                    <Link
                        to={pbdqRoutes.impact}
                        style={impactActive ? activeStyle : undefined}
                        aria-current={impactActive ? 'page' : undefined}
                    >
                        Impact
                    </Link>
                    <Link
                        to={pbdqRoutes.pricing}
                        style={partnersActive ? activeStyle : undefined}
                        aria-current={partnersActive ? 'page' : undefined}
                    >
                        Partners
                    </Link>
                </div>
                <div className={classes.navUtility}>
                    <a href="https://app.poweredbydq.com/login" className={classes.navLogin}>Log in</a>
                    <Link
                        to={pbdqRoutes.getStarted}
                        className={classes.navGetStarted}
                        aria-current={getStartedActive ? 'page' : undefined}
                    >
                        Get started <ArrowRightIcon />
                    </Link>
                </div>
            </nav>
        </header>
    );
};

export default PbdqNav;
